// Responds to the result of a successful Cognito/Auth0 login. appState is
// created by the auth context before login occurs, and contains the path we
// should return to. The auth provider receives this as part of the login
// request and returns it back to us when it's done.

import React from 'react';
import { withStyles, WithStyles } from '@bb-ui/react-library';
import { CircularProgress } from '@bb-ui/react-library/dist/components/Progress';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { useAuthContext } from 'contexts/AuthContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { Redirect, useLocation } from 'react-router-dom';
import { ErrorMessage } from 'components/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { styles } from './Callback.styles';

const CALLBACK_TIMEOUT_MS = 10000;

export const Callback: React.FunctionComponent<WithStyles<typeof styles>> = (props) => {
  const { classes } = props;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const { appState, isAuthenticated, error: authError, signIn } = useAuthContext();
  const { supportRedirectUrl } = useAppConfigContext();
  const { t } = useTranslation();
  const [timedOut, setTimedOut] = React.useState(false);

  // Timeout: if callback hasn't resolved after 10s, show error with retry
  React.useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), CALLBACK_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // 1. URL error param from auth provider (e.g. ?error=access_denied)
  if (searchParams.get('error')) {
    const msg = searchParams.get('error_description') ?? searchParams.get('error') ?? undefined;
    console.error(`Error from Auth provider: "${msg}"`);
    return <ErrorMessage title={t('callback.error.title')} message={msg} variant="fill-window" />;
  }

  // 2. Auth context error (e.g. tokens not acquired from URL hash)
  if (authError) {
    return (
      <ErrorMessage
        title={t('callback.error.title')}
        message={t('callback.error.tokenAcquisitionFailed')}
        variant="fill-window"
      />
    );
  }

  // 3. Authenticated with a return path - redirect back
  if (isAuthenticated && appState?.returnPath) {
    return <Redirect push to={appState.returnPath as string} />;
  }

  // 4. Authenticated but no return path - redirect to home
  if (isAuthenticated) {
    return <Redirect push to="/" />;
  }

  // 5. Support redirect flow
  if (searchParams.get('isSupport') === 'true' && supportRedirectUrl) {
    window.location.assign(
      `${supportRedirectUrl}?tenantId=${searchParams.get(
        'tenantId',
      )}&tenantUriComponent=${searchParams.get('tenantUriComponent')}`,
    );
  }

  // 6. Timed out waiting for auth to complete
  if (timedOut) {
    return (
      <div className={classes.root} data-testid="callback-timeout">
        <Typography variant="h5" gutterBottom>
          {t('callback.error.timeout')}
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          {t('callback.error.timeoutDescription')}
        </Typography>
        <PrimaryButton onClick={() => signIn()} style={{ marginTop: 16 }}>
          {t('callback.error.retry')}
        </PrimaryButton>
      </div>
    );
  }

  // 7. Still loading - show spinner
  return (
    <div className={classes.root} data-testid="callback-page">
      <CircularProgress ariaLabel={t('global.loadingIndicator.loading').toString()} />
    </div>
  );
};

export default withStyles(styles)(Callback);
